/**
 * Sağlayıcı bağlantıları sözleşmesi (v3.1 Dalga 2A, dilim S7 — R0 + S1; ADR-0016 § 1–4). Belge:
 * docs/spec/castinflow/provider-connections.md.
 *
 * - Bağlantı, çalışma alanının bir dış sağlayıcı hesabına (iş mesajlaşma hesabı, sayfa, reklam hesabı …) verdiği yetkidir.
 *   Belirteç yalnız zarf şifreli saklanır (ADR-0011); bu sözleşmedeki hiçbir yanıt belirteç ya da sır taşımaz.
 * - Bu dilim SAĞLAYICIDAN BAĞIMSIZ çekirdektir: gerçek sağlayıcı bağdaştırıcısı yoktur. Yalnız deterministik `simulator`
 *   türü, yalnız üretim dışı ortam bayrağıyla (`PROVIDER_SIMULATOR=on`) bağlanabilir. Gerçek sağlayıcılar katalogda
 *   durum sözlüğüyle (`provider-status.ts`) gerçek sebebine göre görünür; bağlan eylemi yoktur.
 * - Arayüz metinlerinde üçüncü taraf marka adı kullanılmaz (genel kategori adları; settings.md § 9).
 */
import { z } from 'zod';
import { googleAdsConnectStatusSchema } from './google-ads-status.js';
import { planQuotaStatusSchema } from './plans.js';
import { PROVIDER_FEATURE_KINDS, providerConnectionDisplaySchema, providerStatesSchema, } from './provider-status.js';
// ---------------------------------------------------------------------------------------------------------------------
// Türler ve durumlar
// ---------------------------------------------------------------------------------------------------------------------
export const PROVIDER_KINDS = [
    'meta_business',
    'whatsapp_waba',
    'meta_page',
    'meta_ads_account',
    'google_ads_customer',
    'simulator',
];
/** Sahiplik kipi: bugün yalnız müşterinin kendi hesabı (ADR-0016 § 1; ileride `managed`) */
export const PROVIDER_OWNERSHIP_MODES = ['customer_owned'];
/**
 * Durum makinesi (ADR-0016 § 1): `pending → active → expiring → revoked | error → disconnected`.
 *   pending       oluşturuldu, belirteç henüz doğrulanmadı
 *   active        belirteç geçerli
 *   expiring      belirteç geçerli ama süresi yaklaşıyor (yöneticiye uyarı)
 *   revoked       yetki iptal / belirteç geçersiz (sağlayıcı kodu ya da yetki kaldırma olayı)
 *   error         sağlayıcı hesabı kullanılamaz (izin eksik, hesap kapalı …)
 *   disconnected  yönetici bağlantıyı kesti: abonelik iptal edildi, belirteç kripto-silindi (SON durum)
 */
export const PROVIDER_CONNECTION_STATUSES = [
    'pending',
    'active',
    'expiring',
    'revoked',
    'error',
    'disconnected',
];
/**
 * İzinli geçişler. `revoked → active` yalnız yeniden bağlanma (yeni belirteç) ile olur; `disconnected` son durumdur. Aynı
 * duruma "geçiş" geçiş sayılmaz (sağlık denetimi durumu değiştirmeden tazeleyebilir).
 */
export const PROVIDER_STATUS_TRANSITIONS = {
    pending: ['active', 'expiring', 'revoked', 'error', 'disconnected'],
    active: ['expiring', 'revoked', 'error', 'disconnected'],
    expiring: ['active', 'revoked', 'error', 'disconnected'],
    revoked: ['active', 'disconnected'],
    error: ['active', 'expiring', 'revoked', 'disconnected'],
    disconnected: [],
};
export function canTransitionProvider(from, to) {
    return PROVIDER_STATUS_TRANSITIONS[from].includes(to);
}
/** Gönderime açık durumlar (doğrulanmamış `pending` dahil değildir) */
export const PROVIDER_SENDABLE_STATUSES = [
    'active',
    'expiring',
];
export const PROVIDER_HEALTH = ['unknown', 'ok', 'degraded', 'failing'];
/** Bağlantı kesmede sağlayıcı tarafı temizliğin sonucu (abonelik iptali + yetki iptali) */
export const PROVIDER_DISCONNECT_CLEANUP = ['done', 'failed', 'skipped_hold'];
// ---------------------------------------------------------------------------------------------------------------------
// Dış varlıklar
// ---------------------------------------------------------------------------------------------------------------------
/** Bağlantıya bağlı dış varlık türleri; aynı dış varlık AYNI ANDA yalnız bir çalışma alanına bağlanabilir */
export const PROVIDER_ASSET_TYPES = [
    'meta_business',
    'whatsapp_waba',
    'whatsapp_phone_number',
    'meta_page',
    'meta_ads_account',
    'google_ads_customer',
    'simulator_account',
];
export const PROVIDER_ASSET_TYPES_BY_KIND = {
    meta_business: ['meta_business'],
    whatsapp_waba: ['whatsapp_waba', 'whatsapp_phone_number'],
    meta_page: ['meta_page'],
    meta_ads_account: ['meta_ads_account'],
    google_ads_customer: ['google_ads_customer'],
    simulator: ['simulator_account'],
};
/** Dış kimlik biçimi (sağlayıcı kimlikleri sayısal ya da kısa simge dizisidir; boşluk ve denetim karakteri yok) */
export const PROVIDER_EXTERNAL_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{2,127}$/;
/** Arayüzde dış kimlik maskeli gösterilir: yalnız son 4 karakter */
export function maskExternalId(id) {
    if (id.length <= 4)
        return '••••';
    return `••••${id.slice(-4)}`;
}
// ---------------------------------------------------------------------------------------------------------------------
// Gönderim koruması (S1) ve sınırlar
// ---------------------------------------------------------------------------------------------------------------------
/**
 * Gönderim kapısının ret nedenleri. İş BAŞARISIZ sayılmaz: `provider_blocked:<kod>` ile bekler ve yönetici gözden geçirir.
 */
export const PROVIDER_BLOCK_CODES = [
    'connection_missing',
    'connection_pending',
    'connection_revoked',
    'connection_error',
    'connection_disconnected',
    'token_missing',
    'emergency_stop_connection',
    'emergency_stop_tenant',
    'daily_limit_connection',
    'daily_limit_tenant',
    'plan_limit_monthly',
    // v3.1 S10 (ADR-0015/0016 § 4): askıdaki ya da kapanan çalışma alanında giden ileti üretilmez
    'tenant_inactive',
];
/** Gönderim kapısı nedenleri (karantina incelemesi ve bağlantı ekranı aynı metni gösterir) */
export const PROVIDER_BLOCK_TEXT = {
    connection_missing: 'Sağlayıcı bağlantısı bulunamadı; iş gönderilemez, iptal edin',
    connection_pending: 'Sağlayıcı bağlantısı henüz doğrulanmadı',
    connection_revoked: 'Sağlayıcı bağlantısının yetkisi iptal edilmiş; önce yeniden bağlanın',
    connection_error: 'Sağlayıcı hesabı kullanılamıyor; bağlantıyı denetleyin',
    connection_disconnected: 'Sağlayıcı bağlantısı kesildi; iş gönderilemez, iptal edin',
    token_missing: 'Bağlantının belirteci yok; yeniden bağlanın',
    emergency_stop_connection: 'Bu bağlantıda gönderim acil durdurması etkin',
    emergency_stop_tenant: 'Çalışma alanında gönderim acil durdurması etkin',
    daily_limit_connection: 'Bağlantının günlük gönderim sınırı dolu (son 24 saat)',
    daily_limit_tenant: 'Çalışma alanının günlük gönderim sınırı dolu (son 24 saat)',
    plan_limit_monthly: 'Planın bu ayki giden ileti tavanı dolu',
    tenant_inactive: 'Çalışma alanı etkin değil (askıda); giden ileti gönderilmez',
};
/** Karantina gerekçesinden (`provider_blocked:<kod>`) neden kodu; tanınmıyorsa `null` */
export function providerBlockCodeOf(reason) {
    const prefix = 'provider_blocked:';
    if (!reason?.startsWith(prefix))
        return null;
    const code = reason.slice(prefix.length);
    return PROVIDER_BLOCK_CODES.includes(code)
        ? code
        : null;
}
/**
 * Sistem üst sınırı: çalışma alanı başına kesilmemiş bağlantı (plan tavanı bunu aşamaz; `PLAN_SYSTEM_CAPS`). K2'den beri
 * reklam hesabı DIŞINDAKİ bağlantılar içindir; reklam hesaplarının üst sınırı `AD_ACCOUNTS_MAX`.
 */
export const PROVIDER_CONNECTIONS_MAX = 50;
// ---------------------------------------------------------------------------------------------------------------------
// Reklam hesabı alt kotası (K2, 2026-10-11)
// ---------------------------------------------------------------------------------------------------------------------
/**
 * "Hangi bağlantı reklam hesabıdır" kuralının TEK tanımı: bağlantının sağlayıcı TÜRÜ (`provider_connections.provider`).
 * Sosyal medya ve arama reklam hesapları birlikte sayılır (sağlayıcı ayrımı yok). Bu türdeki kesilmemiş bağlantılar ortak
 * "sağlayıcı bağlantısı" sayacına GİRMEZ; kendi plan tavanını (`ad_accounts`) kullanır. Sunucudaki sayım sorgusu
 * (`plan-repo.ts` `countResource`) ve kota kapısı (`connectionQuotaResource`) bu listeyi okur.
 *
 * Genel `simulator` türü listede DEĞİLDİR: üretim dışı deneme bağlantısıdır (ileti, aday olayı ve harcama denemelerinin ortak
 * aracı) ve "diğer bağlantı" sayılır. Reklam hesabı bağlama akışının simülatör kipi ise gerçek akışla AYNI türde
 * (`meta_ads_account`) bağlantı açar ve reklam hesabı kotasına girer.
 */
export const AD_ACCOUNT_CONNECTION_KINDS = [
    'meta_ads_account',
    'google_ads_customer',
];
export function isAdAccountConnection(provider) {
    return AD_ACCOUNT_CONNECTION_KINDS.includes(provider);
}
/** Bağlantı türünün girdiği plan kaynağı (kota kapısı ve kullanım göstergesi aynı eşlemeyi kullanır) */
export function connectionQuotaResource(provider) {
    return isAdAccountConnection(provider) ? 'ad_accounts' : 'provider_connections';
}
/** Sistem üst sınırı: çalışma alanı başına kesilmemiş reklam hesabı (plan tavanı bunu aşamaz; `PLAN_SYSTEM_CAPS`) */
export const AD_ACCOUNTS_MAX = 50;
/**
 * Günlük giden sınırı — SÜRE ESASLI: son 24 saatlik kayan pencere (takvim günü değil; saat diliminden ve yaz saatinden
 * bağımsız). Değerler sağlayıcı kademesi değil, Castinflow'un güvenlik varsayılanıdır; yönetici 0 ile üst sınır arasında
 * değiştirebilir (`null` = varsayılan). 0 = gönderim kapalı.
 */
export const PROVIDER_OUTBOUND_LIMITS = {
    windowHours: 24,
    tenantDailyDefault: 1000,
    connectionDailyDefault: 500,
    dailyMax: 100_000,
};
/** Belirteç bu kadar gün içinde sona erecekse bağlantı `expiring` olur */
export const PROVIDER_EXPIRING_WITHIN_DAYS = 7;
/** Sağlık denetimi aralığı (bağlantı başına) */
export const PROVIDER_HEALTH_CHECK_INTERVAL_MINUTES = 60;
/** Sağlayıcı olay alma ucu sınırları */
export const PROVIDER_HOOK_LIMITS = {
    /** Ham gövde üst sınırı (bayt) */
    maxBodyBytes: 256 * 1024,
    /** Tek istekte en çok olay */
    maxEventsPerRequest: 20,
    /** Bağlantı başına dakikalık olay sınırı (gelen yavaşlatma) */
    connectionPerMinute: 300,
    /** Çalışma alanı başına dakikalık olay sınırı */
    tenantPerMinute: 600,
    /** İmzası doğrulanamayan / çözülemeyen istek: IP başına pencere içinde bu sayıdan sonra geçici ret */
    ipFailuresPerWindow: 20,
    ipFailureWindowSeconds: 600,
    /** Ham yükün saklanma süresi (gün; `purge_after`) */
    rawPayloadRetentionDays: 7,
};
/**
 * Askıdaki çalışma alanı (ADR-0015/0016 § 4): imzası doğrulanan olay saklanır ama işlenmez. Tavan dolunca yeni olay
 * reddedilir ve SAYILIR (veri kaybı ihtimali görünür); süre dolan bekleyen olay işlenmeden kapanır ve sayılır.
 */
export const PROVIDER_SUSPENDED_STORAGE = {
    maxEvents: 5000,
    maxBytes: 50 * 1024 * 1024,
    retentionDays: 30,
};
// ---------------------------------------------------------------------------------------------------------------------
// Simülatör (yalnız üretim dışı)
// ---------------------------------------------------------------------------------------------------------------------
/**
 * Simülatörün "sağlayıcı tarafı" senaryosu (deterministik): belirteç denetimi ve gönderim bu değere göre sonuç verir.
 *   valid     belirteç geçerli (süresi 60 gün sonra)
 *   expiring  belirteç geçerli, süresi 3 gün sonra
 *   invalid   belirteç iptal edilmiş (sağlayıcı yetkiyi kaldırdı) → `revoked`
 *   error     hesap kullanılamıyor → `error`
 *   timeout   belirteç geçerli ama gönderimde sağlayıcı yanıt vermiyor → ileti sonucu BELİRSİZ (`unknown`; v3.1 S10)
 */
export const SIMULATOR_SCENARIOS = ['valid', 'expiring', 'invalid', 'error', 'timeout'];
/** Simülatör gelen olay türleri */
export const SIMULATOR_EVENT_TYPES = ['message', 'status', 'lead', 'echo_request'];
/** İleti fiyat kategorileri (durum olaylarındaki fiyat nesnesi; tahmini maliyet bilgisidir, fatura sağlayıcıdadır) */
export const MESSAGE_PRICING_CATEGORIES = [
    'marketing',
    'utility',
    'authentication',
    'service',
];
/** Simülatörün giden ileti durum olayları (v3.1 S10; sağlayıcı durum olaylarının karşılığı) */
export const SIMULATOR_DELIVERY_STATUSES = ['sent', 'delivered', 'read', 'failed'];
/** Simülatör olay gövdesi (imzalı; `X-Castinflow-Simulator-Signature: sha256=<hex>`) */
export const simulatorHookEventSchema = z
    .object({
    eventId: z.string().regex(/^[A-Za-z0-9._:-]{8,128}$/),
    assetType: z.literal('simulator_account'),
    assetId: z.string().regex(PROVIDER_EXTERNAL_ID_RE),
    type: z.enum(SIMULATOR_EVENT_TYPES),
    occurredAt: z.iso.datetime({ offset: true }),
    data: z
        .object({
        messageId: z.string().max(128).optional(),
        mediaBytes: z
            .number()
            .int()
            .min(0)
            .max(100 * 1024 * 1024)
            .optional(),
        pricing: z
            .object({ category: z.enum(MESSAGE_PRICING_CATEGORIES), billable: z.boolean() })
            .strict()
            .optional(),
        text: z.string().max(4096).optional(),
        // v3.1 S10 (konuşma çekirdeği): gelen iletinin göndereni (telefon E.164 / e-posta / sağlayıcı kimliği) ve görünen adı
        from: z.string().min(3).max(128).optional(),
        fromName: z.string().max(200).optional(),
        // v3.1 S10: giden iletinin teslim durumu olayı (`messageId` = sağlayıcının ileti kimliği)
        status: z.enum(SIMULATOR_DELIVERY_STATUSES).optional(),
        errorCode: z
            .string()
            .regex(/^[a-z][a-z0-9_]{1,59}$/)
            .optional(),
        // v3.1 S10: gelen ileti ekleri (yalnız simülatör; içerik base64, işçi indirir ve tarar)
        attachments: z
            .array(z
            .object({
            fileName: z.string().min(1).max(255),
            mimeType: z.string().min(3).max(127),
            contentBase64: z.string().max(400_000),
        })
            .strict())
            .max(5)
            .optional(),
    })
        .strict()
        .prefault({}),
})
    .strict();
export const simulatorHookBodySchema = z
    .object({ events: z.array(simulatorHookEventSchema).min(1).max(20) })
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// Katalog (dürüst): hangi türün hangi gerçek sebeple bağlanamadığını SUNUCU söyler
// ---------------------------------------------------------------------------------------------------------------------
/**
 * Katalogdaki türler ve ekran sırası (simülatör katalogda yok). Bir türün "Yakında" / "Yapılandırılmadı" / "Onay bekliyor"
 * durumu burada SABİT DEĞİLDİR: v3.2'de burada sabit bir etiket vardı ve kodu teslim edilmiş arama reklamları "Yakında"
 * yazıyordu. Durum ve sebep sunucuda yapılandırmadan türetilir (`providerStates`; `provider-status.ts`).
 */
export const PROVIDER_CATALOG = PROVIDER_FEATURE_KINDS.map((kind) => ({ kind }));
// ---------------------------------------------------------------------------------------------------------------------
// API sözleşmesi
// ---------------------------------------------------------------------------------------------------------------------
const reason = z.string().trim().min(5, 'gerekçe en az 5 karakter olmalı').max(1000);
const dailyLimit = z.number().int().min(0).max(PROVIDER_OUTBOUND_LIMITS.dailyMax).nullable();
export const providerAssetViewSchema = z.object({
    id: z.string(),
    assetType: z.enum(PROVIDER_ASSET_TYPES),
    /** Maskeli dış kimlik (son 4 karakter) */
    externalIdMasked: z.string(),
    displayName: z.string().nullable(),
    boundAt: z.string(),
    releasedAt: z.string().nullable(),
});
const emergencyStopSchema = z.object({
    active: z.boolean(),
    since: z.string().nullable(),
    reason: z.string().nullable(),
});
export const providerConnectionViewSchema = z.object({
    id: z.string(),
    provider: z.enum(PROVIDER_KINDS),
    ownership: z.enum(PROVIDER_OWNERSHIP_MODES),
    displayName: z.string(),
    status: z.enum(PROVIDER_CONNECTION_STATUSES),
    statusReason: z.string().nullable(),
    statusChangedAt: z.string(),
    health: z.enum(PROVIDER_HEALTH),
    lastHealthCheckAt: z.string().nullable(),
    lastEventAt: z.string().nullable(),
    tokenExpiresAt: z.string().nullable(),
    /** Belirteç saklı mı (değer ASLA dönmez) */
    hasToken: z.boolean(),
    scopes: z.array(z.string()),
    assets: z.array(providerAssetViewSchema),
    emergencyStop: emergencyStopSchema,
    /** Bağlantıya özel günlük sınır (`null` = varsayılan) ve etkin değer */
    dailyOutboundLimit: dailyLimit,
    effectiveDailyLimit: z.number().int(),
    /** Son 24 saatte gönderime verilen ileti */
    sentLast24h: z.number().int(),
    /** Bu bağlantı için `provider_blocked` bekleyen iş sayısı */
    blockedJobs: z.number().int(),
    simulatorScenario: z.enum(SIMULATOR_SCENARIOS).nullable(),
    /**
     * v3.3 DEMO: demo bağlantısı (yalnız demo çalışma alanında, yalnız örnek veri yüklemesiyle oluşur). Hiçbir sağlayıcıya bağlı
     * değildir, belirteç taşımaz, yönetim eylemi (denetle / yeniden bağlan / kes) kabul etmez; ekranda "Demo" olarak
     * etiketlenir ve gerçek bir bağlantı gibi "bağlı" gösterilmez.
     */
    isDemo: z.boolean().optional(),
    disconnectedAt: z.string().nullable(),
    disconnectCleanup: z.enum(PROVIDER_DISCONNECT_CLEANUP).nullable(),
    createdAt: z.string(),
    /**
     * v3.3: bağlantının durum sözlüğündeki durumu ve (iş mesajlaşma hesabında) kurulum durumu — sunucu türetir. Eski sunucu
     * yanıtında yoktur (arayüz o zaman satırın `status` değerinden türetir: `providerConnectionDisplayOf`).
     */
    display: providerConnectionDisplaySchema.optional(),
});
export const providerOutboundControlsViewSchema = z.object({
    emergencyStop: emergencyStopSchema,
    dailyOutboundLimit: dailyLimit,
    effectiveDailyLimit: z.number().int(),
    sentLast24h: z.number().int(),
});
/** Askı dönemi saklama durumu (çalışma alanı) */
export const providerSuspensionViewSchema = z.object({
    /** Askıda saklanan ve henüz işlenmemiş olay */
    heldEvents: z.number().int(),
    heldBytes: z.number().int(),
    /** Tavan dolduğu için reddedilen olay (veri kaybı ihtimali) — tüm zamanlar */
    rejectedEvents: z.number().int(),
    /** Saklama süresi dolduğu için işlenmeden kapanan olay — tüm zamanlar */
    expiredEvents: z.number().int(),
    caps: z.object({
        maxEvents: z.number().int(),
        maxBytes: z.number().int(),
        retentionDays: z.number().int(),
    }),
});
export const providerConnectionListSchema = z.object({
    connections: z.array(providerConnectionViewSchema),
    controls: providerOutboundControlsViewSchema,
    suspension: providerSuspensionViewSchema,
    /** Bütün bağlantılarda `provider_blocked` bekleyen iş */
    blockedJobs: z.number().int(),
    /**
     * `pageShortcut` (v3.3 DEMO): sayfa kimliği yazılarak DOĞRUDAN simülatör sayfa bağlantısı oluşturma kısayolu açık mı.
     * Yalnız izole testlerde (`SIMULATOR_PAGE_SHORTCUT=on`, üretim kipinde yasak) `true`; normal çalışma zamanında form ve uç
     * yoktur (simülatör açık olsa da). Eski sunucu yanıtında alan yoktur → kapalı sayılır.
     */
    simulator: z.object({ enabled: z.boolean(), pageShortcut: z.boolean().optional() }),
    /**
     * v3.1 CA-4: reklam hesabı bağlama akışı bu süreçte GERÇEK kipte açık mı (`META_ADS=on` + uygulama yapılandırması tam).
     * Kapalıysa arayüz "onay bekliyor" der; simülatör akışı `simulator.enabled` ile ayrıca açılır.
     * `quota` (K2): reklam hesabı alt kotasının anlık durumu — kullanılan / etkin tavan / kalan yer (`null` = sınırsız).
     * Bilgilendirir; karar bağlama anında sunucudadır. Yeniden yetkilendirme yer harcamaz. Eski sunucu yanıtında alan yoktur
     * (arayüz o zaman kota göstermez; sunucu kararı değişmez).
     */
    adConnect: z.object({ live: z.boolean(), quota: planQuotaStatusSchema.optional() }),
    /**
     * v3.2 D: arama reklamları hesabı bağlama bölümünün durumu — gerçek kip açık mı, bildirilen erişim seviyesi ve uygulama
     * doğrulaması (kapalı durumlar ayrı ayrı açıklanır), reklam hesabı alt kotası (sosyal medya ile ORTAK). Eski sunucu yanıtında
     * alan yoktur (arayüz o zaman bölümü "henüz açılmadı" olarak gösterir).
     */
    googleAdsConnect: googleAdsConnectStatusSchema.optional(),
    /**
     * v3.2: sosyal medya sayfası bağlama (gerçek sayfa yetkilendirme) bu süreçte GERÇEK kipte açık mı (`META_LEAD_ADS=on` +
     * uygulama yapılandırması + işletme girişi yapılandırma kimliği). Kapalıysa arayüz dürüst bekleme ekranı gösterir; simülatör
     * akışı `simulator.enabled` ile ayrıca açılır. `quota`: "diğer bağlantılar" sayacının anlık durumu (`provider_connections`;
     * reklam hesabı kotası DEĞİL). Bilgilendirir; karar bağlama anında sunucudadır. Eski sunucu yanıtında alan yoktur.
     */
    pageConnect: z.object({ live: z.boolean(), quota: planQuotaStatusSchema.optional() }).optional(),
    /**
     * v3.3: katalogdaki her türün durum sözlüğündeki durumu ve GERÇEK sebebi — sunucu yapılandırmadan türetir, istemci tahmin
     * etmez. Yukarıdaki `live` alanları geriye uyum içindir ve bu alanla çelişmez. Eski sunucu yanıtında yoktur.
     */
    providerStates: providerStatesSchema.optional(),
});
export const providerEventCountsSchema = z.object({
    received: z.number().int(),
    processed: z.number().int(),
    heldSuspended: z.number().int(),
    expired: z.number().int(),
});
export const providerConnectionDetailSchema = z.object({
    connection: providerConnectionViewSchema,
    events: providerEventCountsSchema,
});
/** Oluşturma: bu dilimde yalnız simülatör (gerçek sağlayıcılar onay bekliyor) */
export const providerConnectionCreateInputSchema = z
    .object({
    provider: z.enum(PROVIDER_KINDS),
    displayName: z.string().trim().min(1).max(120),
    externalId: z.string().trim().regex(PROVIDER_EXTERNAL_ID_RE, 'geçersiz dış hesap kimliği'),
    scenario: z.enum(SIMULATOR_SCENARIOS).default('valid'),
})
    .strict();
export const providerDisconnectInputSchema = z
    .object({ confirm: z.literal(true, { message: 'açık onay gerekli' }), reason })
    .strict();
/** Acil durdurma ve günlük sınır (bağlantı ya da çalışma alanı düzeyi). Acil durdurma değişikliği gerekçe ister. */
export const providerControlsInputSchema = z
    .object({
    emergencyStop: z.boolean().optional(),
    dailyOutboundLimit: dailyLimit.optional(),
    reason: reason.optional(),
})
    .strict()
    .refine((v) => v.emergencyStop !== undefined || v.dailyOutboundLimit !== undefined, {
    message: 'değiştirilecek bir ayar seçin',
})
    .refine((v) => v.emergencyStop === undefined || v.reason !== undefined, {
    message: 'acil durdurma değişikliği için gerekçe gerekli',
    path: ['reason'],
});
export const simulatorScenarioInputSchema = z
    .object({ scenario: z.enum(SIMULATOR_SCENARIOS) })
    .strict();
export const simulatorEventInputSchema = z
    .object({
    type: z.enum(SIMULATOR_EVENT_TYPES),
    mediaBytes: z
        .number()
        .int()
        .min(0)
        .max(100 * 1024 * 1024)
        .optional(),
    pricingCategory: z.enum(MESSAGE_PRICING_CATEGORIES).optional(),
    // v3.1 S10: gelen ileti içeriği ve göndereni; giden iletinin durum olayı
    text: z.string().trim().min(1).max(4096).optional(),
    from: z.string().trim().min(3).max(128).optional(),
    fromName: z.string().trim().max(200).optional(),
    messageId: z.string().trim().min(1).max(128).optional(),
    status: z.enum(SIMULATOR_DELIVERY_STATUSES).optional(),
})
    .strict();
/** Olay alma sonucu (yönlendirici yanıtı ve simülatör olay ucu) */
export const PROVIDER_HOOK_OUTCOMES = [
    'accepted',
    'duplicate',
    'stored_suspended',
    'rejected_suspended_cap',
    'unresolved',
    'rate_limited',
];
export const providerHookResultSchema = z.object({
    received: z.number().int(),
    outcomes: z.record(z.string(), z.number().int()),
});
export const providerHealthCheckResultSchema = z.object({
    connection: providerConnectionViewSchema,
    result: z.enum(['valid', 'expiring', 'invalid', 'error', 'unknown']),
});
//# sourceMappingURL=provider-connections.js.map